import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";

import { InvoiceCsvDownload, type CenterInvoiceCsv } from "./invoice-csv-download.js";
import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** できること（<c>GET /api/admin-center</c>。サーバの CenterMenuViewModel と同じ欄）。 */
export interface CenterMenu {
    readonly canManageInvoices: boolean;
    readonly canManageAccounts: boolean;
    readonly canManagePermissions: boolean;
    /**
     * アクティビティログの入口を出すか。<b>返さなければ出す</b>——この画面を開けるのは運営の権限のどれかを持つ人だけで、
     * アクティビティログもその人たちが開ける（開けるかはサーバが毎回判定する）。
     */
    readonly canSeeActivity?: boolean;
    /** 課金の結果の入口を出すか（請求の権限。<b>返さなければ出さない</b>——開けるかはサーバが毎回判定する）。 */
    readonly canSeePayments?: boolean;
    /** 請求の CSV の欄（請求の権限を持つ人にだけ返る。<b>返さなければ出さない</b>——権限は画面で判定しない）。 */
    readonly invoiceCsv?: CenterInvoiceCsv | null;
}

/** 1 つの欄。 */
function MenuCard({ title, lead, className, children }: {
    readonly title: string;
    readonly lead: string;
    readonly className?: string;
    readonly children: ReactElement;
}): ReactElement {
    return (
        <section className={["card", className, "p-6"].filter(Boolean).join(" ")}>
            <h2 className="section-title">{title}</h2>
            <p className="mb-4 text-sm text-slate-500">{lead}</p>
            {children}
        </section>
    );
}

/**
 * 管理センターの入口。<b>できることを並べるだけ</b>（検索結果は置かない——件数で位置が動く）。
 * <b>持っていない権限の欄は出さない。</b>権限が 1 つも無ければ API が 404 で断る。
 */
export function MenuPage(): ReactElement | null {
    const data = useApiData<CenterMenu>(adminCenterApi.menu);
    usePageMeta(titleFor(data, "管理センター"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="管理センター" />;
    }
    const menu = data.value;

    return (
        <div className="page max-w-3xl">
            <h1 className="page-title">管理センター</h1>
            <p className="page-lead">
                <strong className="text-ink-800">ご自身に許可されている操作だけ</strong>を表示しています。
            </p>

            {/* 契約を調べるのが起点。**権限に関わらず出す**——中身は次の画面で権限ごとに出し分ける */}
            <MenuCard
                title="契約情報"
                lead="企業名・システム名・契約 ID・利用者のメールアドレスで探せます。プラン・状態・お支払方法でも絞り込めます。"
                className="mb-4"
            >
                <Link to={adminCenterUrls.tenants} className="btn-secondary">契約を探す</Link>
            </MenuCard>

            {menu.canSeeActivity ?? true
                ? (
                    <MenuCard
                        title="アクティビティログ"
                        lead="誰が・いつ・何をしたかの記録を、期間・種別・操作した人や対象のメールアドレスか名前で探せます。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.activity()} className="btn-secondary">アクティビティログを見る</Link>
                    </MenuCard>
                )
                : null}

            {menu.canManageInvoices
                ? (
                    <MenuCard
                        title="送ったメールの確認"
                        lead="実際に送ったメールを宛先・件名・期間で探し、本文を確認できます。リンクの鍵は伏せて表示します。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.mails} className="btn-secondary">送ったメールを探す</Link>
                    </MenuCard>
                )
                : null}

            {menu.canSeePayments ?? false
                ? (
                    <MenuCard
                        title="課金の結果"
                        lead="PAY.JP の毎月の請求の結果（成功・失敗・返金）と停止中の定期課金、ポータルが PAY.JP を呼んだ記録（カードの登録・定期課金の作成と解除など）を、期間・結果・契約で探せます。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.charges()} className="btn-secondary">課金の結果を見る</Link>
                    </MenuCard>
                )
                : null}

            {/* 全契約の 1 か月分。開く前に確認のダイアログで聞く（期間が終わった月の請求書が固定される） */}
            {menu.invoiceCsv === undefined || menu.invoiceCsv === null
                ? null
                : (
                    <MenuCard
                        title="請求の CSV"
                        lead="全契約の請求を、月を選んで 1 つの CSV でダウンロードできます。他社の金額が並ぶので、扱いに注意してください。"
                        className="mb-4"
                    >
                        <InvoiceCsvDownload csv={menu.invoiceCsv} />
                    </MenuCard>
                )}

            {menu.canManageInvoices
                ? (
                    <MenuCard
                        title="見積の検索"
                        lead="料金プランのページから作られた見積書を、番号・宛名・発行日・プランで探し、開いて印刷できます。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.estimates()} className="btn-secondary">見積を探す</Link>
                    </MenuCard>
                )
                : null}

            {/* 解約から 30 日以内なら元に戻せる（解約は契約 1 件の画面から。解約した契約はそこで開けないので入口に置く） */}
            {menu.canManageInvoices
                ? (
                    <MenuCard
                        title="契約の復元"
                        lead="解約から 30 日以内の契約を一覧から選んで、解約の前の状態に戻せます。カード払いだった契約は請求書払いで戻ります。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.restore} className="btn-secondary">解約した契約を復元する</Link>
                    </MenuCard>
                )
                : null}

            {/* ロックはポータルとプリザンター（/fs）で共有される。解除の手段が要る */}
            {menu.canManageAccounts
                ? (
                    <MenuCard
                        title="ロックの解除"
                        lead="パスワードを続けて間違えると 30 分間ロックされます。待たずに解除できます。"
                        className="mb-4"
                    >
                        <Link to={adminCenterUrls.lockout} className="btn-secondary">ロックを解除する</Link>
                    </MenuCard>
                )
                : null}

            {menu.canManagePermissions
                ? (
                    <MenuCard
                        title="権限管理"
                        lead="この画面でできることを、どなたに許可するかを設定します。"
                    >
                        <Link to={adminCenterUrls.permissions} className="btn-secondary">権限を設定する</Link>
                    </MenuCard>
                )
                : null}
        </div>
    );
}
